// barcodeService.js — single source of truth for barcode lookup + assignment
// (Brian's field findings, 2026-07-14).
//
// THE BUG THIS KILLS: assigning a barcode to product B used to leave it on
// product A too — scans kept finding the old mistake. assignBarcode() now
// strips the code (and its UPC/EAN variants) from every other product in the
// same operation.

import { db } from "../config/firebase";
import { doc, updateDoc, deleteField } from "firebase/firestore";
import { invalidateProductCache } from "./dataCache";

// The same physical label can scan as 12 digits (UPC-A) or 13 with a leading
// zero (EAN-13) depending on the scanner, so match all plausible forms.
export function barcodeVariants(code) {
  const raw = String(code).trim();
  const variants = new Set([raw]);
  const digits = raw.replace(/\D/g, "");
  if (digits) {
    variants.add(digits);
    const noZeros = digits.replace(/^0+/, "");
    if (noZeros) variants.add(noZeros);
    if (digits.length === 12) variants.add("0" + digits);
    if (digits.length === 13 && digits.startsWith("0")) variants.add(digits.slice(1));
  }
  return [...variants];
}

/** The product (if any) currently holding this barcode, variants included. */
export function findByBarcode(products, code) {
  const variants = new Set(barcodeVariants(code));
  return products.find(p => p.barcode && variants.has(String(p.barcode))) || null;
}

/**
 * Assigns `code` to `sku`, removing it from any other product that holds it
 * (or a variant of it). Returns the skus that were cleared.
 */
export async function assignBarcode(sku, code, products) {
  const variants = new Set(barcodeVariants(code));
  const holders = products.filter(p =>
    p.sku !== sku && p.barcode && variants.has(String(p.barcode)));
  for (const h of holders) {
    await updateDoc(doc(db, "products", h.sku), {
      barcode: deleteField(),
      barcodeSource: deleteField()
    });
  }
  await updateDoc(doc(db, "products", sku), {
    barcode: String(code),
    barcodeSource: "portal"
  });
  invalidateProductCache();
  return holders.map(h => h.sku);
}

/** Removes a product's barcode entirely. */
export async function clearBarcode(sku) {
  await updateDoc(doc(db, "products", sku), {
    barcode: deleteField(),
    barcodeSource: deleteField()
  });
  invalidateProductCache();
}
